import { ChefHat, BedDouble, Tv, Sofa, BookOpen, Layers, Footprints, Wine, UtensilsCrossed, Bath, DoorOpen, Armchair } from 'lucide-react';

// Design Variants/Styles - Applied as multiplier to material and labor costs
export const VARIANTS = [
  { 
    value: 'minimalist', 
    label: 'Minimalist', 
    description: 'Clean lines with elegant matte finishes',
    materials: 'Quality laminate finish',
    color: 'bg-gray-100 text-gray-700',
    multiplier: 1.0
  },
  { 
    value: 'modern', 
    label: 'Modern', 
    description: 'Contemporary aesthetics with premium finishes',
    materials: 'High-gloss acrylic finish',
    color: 'bg-blue-100 text-blue-700',
    multiplier: 1.2
  },
  { 
    value: 'traditional', 
    label: 'Traditional', 
    description: 'Timeless craftsmanship with classic details',
    materials: 'Natural veneer with PU finish',
    color: 'bg-amber-100 text-amber-700',
    multiplier: 1.5
  },
  { 
    value: 'luxury', 
    label: 'Luxury', 
    description: 'Premium materials with smart automation',
    materials: 'Italian finishes with automation',
    color: 'bg-purple-100 text-purple-700',
    multiplier: 2.0
  },
];

// Room Types - For room templates
export const ROOM_TEMPLATE_TYPES = [
  {
    value: 'bedroom',
    label: 'Bedroom',
    icon: BedDouble,
    description: 'Master/Guest bedroom with wardrobe and accessories',
    defaultItems: [
      { item_type: 'wardrobe', item_name: 'Wardrobe', frontage_length: 8, height: 9.5 },
      { item_type: 'dresser', item_name: 'Dresser Unit', frontage_length: 4, height: 4, add_mirror: true },
    ]
  },
  {
    value: 'kids_room',
    label: 'Kids Room',
    icon: BedDouble,
    description: 'Child-friendly room with wardrobe and study desk',
    defaultItems: [
      { item_type: 'wardrobe', item_name: 'Wardrobe', frontage_length: 6, height: 9.5 },
      { item_type: 'study_desk', item_name: 'Study Desk', frontage_length: 4, height: 3 },
    ]
  },
  {
    value: 'living_room',
    label: 'Living Room',
    icon: Sofa,
    description: 'Entertainment area with TV unit and feature wall',
    defaultItems: [
      { item_type: 'tv_unit', item_name: 'TV Unit', frontage_length: 8, height: 6 },
      { item_type: 'feature_wall', item_name: 'Feature Wall', frontage_length: 10, height: 9.5 },
    ]
  },
  {
    value: 'drawing_room',
    label: 'Drawing Room',
    icon: Armchair,
    description: 'Formal seating area with showcase and display',
    defaultItems: [
      { item_type: 'feature_wall', item_name: 'Display Unit', frontage_length: 8, height: 7 },
      { item_type: 'crockery_unit', item_name: 'Showcase', frontage_length: 5, height: 7 },
    ]
  },
  {
    value: 'entry_hall',
    label: 'Entry Hall',
    icon: DoorOpen,
    description: 'Welcoming entrance with shoe storage',
    defaultItems: [
      { item_type: 'shoe_rack', item_name: 'Shoe Rack', frontage_length: 4, height: 5 },
      { item_type: 'feature_wall', item_name: 'Entry Panel', frontage_length: 4, height: 9.5 },
    ]
  },
  {
    value: 'pooja_room',
    label: 'Pooja Room',
    icon: Layers,
    description: 'Sacred space with storage and backlit panel',
    defaultItems: [
      { item_type: 'pooja', item_name: 'Pooja Unit', frontage_length: 4, height: 7, add_profile_lighting: true },
    ]
  },
  {
    value: 'study_room',
    label: 'Study Room',
    icon: BookOpen,
    description: 'Work/study space with desk and storage',
    defaultItems: [
      { item_type: 'study_desk', item_name: 'Study Desk', frontage_length: 6, height: 3 },
      { item_type: 'wardrobe', item_name: 'Book Shelf', frontage_length: 4, height: 7 },
    ]
  },
  {
    value: 'kitchen',
    label: 'Kitchen',
    icon: ChefHat,
    description: 'Modular kitchen with package selection',
    isKitchen: true
  },
  {
    value: 'other',
    label: 'Custom Room',
    icon: Layers,
    description: 'Configure room with custom items',
    defaultItems: []
  }
];

// Sub-item types that can be added within a room
export const SUB_ITEM_TYPES = [
  { 
    value: 'wardrobe', 
    label: 'Wardrobe', 
    icon: BedDouble, 
    defaultFrontage: 8,
    defaultHeight: 9.5,
    description: 'Includes shelves, hanging space, drawers, hinges'
  },
  { 
    value: 'walk_in_closet', 
    label: 'Walk-in Closet', 
    icon: BedDouble, 
    defaultFrontage: 12,
    defaultHeight: 9.5,
    description: 'Large walk-in wardrobe with hanging, shelves, drawers, island'
  },
  { 
    value: 'dresser', 
    label: 'Dresser Unit', 
    icon: Bath, 
    defaultFrontage: 4,
    defaultHeight: 4,
    description: 'Includes mirror, drawers, storage compartments'
  },
  { 
    value: 'tv_unit', 
    label: 'TV Unit', 
    icon: Tv, 
    defaultFrontage: 8,
    defaultHeight: 6,
    description: 'Includes cabinets, shelves, cable management'
  },
  { 
    value: 'feature_wall', 
    label: 'Feature Wall', 
    icon: Sofa, 
    defaultFrontage: 10,
    defaultHeight: 9.5,
    description: 'Decorative paneling with optional lighting'
  },
  { 
    value: 'study_desk', 
    label: 'Study Desk', 
    icon: BookOpen, 
    defaultFrontage: 5,
    defaultHeight: 3,
    description: 'Includes desk, drawers, overhead storage'
  },
  { 
    value: 'pooja', 
    label: 'Pooja Unit', 
    icon: Layers, 
    defaultFrontage: 4,
    defaultHeight: 7,
    description: 'Lower cabinet + glass upper with backlit panel'
  },
  { 
    value: 'shoe_rack', 
    label: 'Shoe Rack', 
    icon: Footprints, 
    defaultFrontage: 4,
    defaultHeight: 5,
    description: 'Includes shelves, drawers, pull-out trays'
  },
  { 
    value: 'crockery_unit', 
    label: 'Crockery Unit', 
    icon: UtensilsCrossed, 
    defaultFrontage: 5,
    defaultHeight: 7,
    description: 'Includes glass doors, shelves, drawers'
  },
  { 
    value: 'bar_unit', 
    label: 'Bar Unit', 
    icon: Wine, 
    defaultFrontage: 4,
    defaultHeight: 6,
    description: 'Includes wine rack, glass holder, counter'
  },
  { 
    value: 'vanity', 
    label: 'Vanity Unit', 
    icon: Bath, 
    defaultFrontage: 4,
    defaultHeight: 3,
    description: 'Includes mirror, drawers, countertop'
  },
];

// Kitchen Package Configurations
export const KITCHEN_PACKAGES = [
  {
    value: 'basic',
    name: 'Basic Package',
    priceIndicator: '₹',
    description: 'Functional kitchen with quality basics',
    features: [
      'Basic lower & upper cabinets with doors',
      'Standard HDHMR carcass with laminate finish',
      'Basic hinges and pulls',
      'Standard box drawers',
      'Basic Granite counter top'
    ],
    color: 'bg-gray-50 border-gray-200'
  },
  {
    value: 'essential',
    name: 'Essential Package',
    priceIndicator: '₹₹',
    description: 'Upgraded finishes with functional improvements',
    features: [
      'Enhanced lower cabinets with up to 4 tandem drawers',
      'Soft-close doors on upper cabinets',
      'BWP Plywood carcass with upgraded laminate',
      'Standard soft-close hinges',
      'Level 1 Granite counter top',
      'Cutlery tray & Plate organizer included'
    ],
    color: 'bg-blue-50 border-blue-200'
  },
  {
    value: 'preferred',
    name: 'Preferred Package',
    priceIndicator: '₹₹₹',
    description: 'Sleek, glossy finishes with premium functionality',
    features: [
      'Premium lower cabinets with branded tandem boxes',
      'Dual-tone upper cabinets with glass door options',
      'BWP Plywood with high-gloss acrylic finish',
      'Branded soft-close hinges (Hettich/Ebco)',
      'Level 2 Granite or Standard Quartz counter top',
      'Corner carousel & Tall unit options'
    ],
    color: 'bg-green-50 border-green-200'
  },
  {
    value: 'gourmet_plus',
    name: 'Gourmet Plus Package',
    priceIndicator: '₹₹₹₹',
    description: 'All premium upgrades for the discerning chef',
    features: [
      'Premium lower cabinets with full extension tandem boxes',
      'Designer upper cabinets with profile lighting',
      'Premium plywood with smooth acrylic/lacquer finish',
      'Premium Blum/Hettich soft-close hardware',
      'Level 3 Granite or Premium Quartz counter top',
      'Handleless design, Magic corners, Appliance garage'
    ],
    color: 'bg-purple-50 border-purple-200'
  }
];

// Room type configurations (legacy - for backward compatibility)
export const ROOM_TYPES = [
  { 
    value: 'kitchen', 
    label: 'Kitchen', 
    icon: ChefHat, 
    inputLabel: 'Total Running Length (ft)',
    inputHint: 'Total counter length (e.g., L-shape: 10ft + 6ft = 16ft)',
    defaultFrontage: 12,
    defaultHeight: 9.5,
    showHeight: false,
    hasPackages: true
  },
  { 
    value: 'bedroom', 
    label: 'Wardrobe', 
    icon: BedDouble, 
    inputLabel: 'Frontage Length (ft)',
    inputHint: 'Width of wardrobe frontage',
    defaultFrontage: 8,
    defaultHeight: 9.5,
    showHeight: true,
    hasPackages: false
  },
  { 
    value: 'tv_unit', 
    label: 'TV Unit', 
    icon: Tv, 
    inputLabel: 'Width (ft)',
    inputHint: 'Width of TV unit',
    defaultFrontage: 8,
    defaultHeight: 6,
    showHeight: true,
    hasPackages: false
  },
  { 
    value: 'living_room', 
    label: 'Feature Wall', 
    icon: Sofa, 
    inputLabel: 'Width (ft)',
    inputHint: 'Width of feature wall',
    defaultFrontage: 10,
    defaultHeight: 9.5,
    showHeight: true,
    hasPackages: false
  },
  { 
    value: 'study', 
    label: 'Study Desk', 
    icon: BookOpen, 
    inputLabel: 'Desk Length (ft)',
    inputHint: 'Length of study desk',
    defaultFrontage: 5,
    defaultHeight: 3,
    showHeight: false,
    hasPackages: false
  },
  { 
    value: 'pooja', 
    label: 'Pooja Unit', 
    icon: Layers, 
    inputLabel: 'Width (ft)',
    inputHint: 'Width of pooja unit',
    defaultFrontage: 4,
    defaultHeight: 7,
    showHeight: true,
    hasPackages: false
  },
];

// Create default sub-item
export const createDefaultSubItem = (itemType = 'wardrobe') => {
  const config = SUB_ITEM_TYPES.find(t => t.value === itemType) || SUB_ITEM_TYPES[0];
  return {
    item_type: itemType,
    item_name: config.label,
    frontage_length: config.defaultFrontage,
    height: config.defaultHeight,
    add_profile_lighting: false,
    add_mirror: itemType === 'dresser' || itemType === 'vanity',
    selected: true // For template item selection
  };
};

// Create room from template
export const createRoomFromTemplate = (templateType, roomNumber, globalStyle = 'modern') => {
  const template = ROOM_TEMPLATE_TYPES.find(t => t.value === templateType);
  
  if (!template) {
    return createDefaultRoom('other', roomNumber, globalStyle);
  }
  
  if (template.isKitchen) {
    return {
      id: `kitchen-${Date.now()}`,
      room_type: 'kitchen',
      room_name: 'Kitchen',
      frontage_length: 12,
      height: 9.5,
      kitchen_package: 'basic',
      tall_uppers: false,
      add_loft: false,
      shutter_addons: { glass: false, traditional: false, acrylic: false },
      sub_items: [],
      include_false_ceiling: false,
      false_ceiling_sqft: 0,
      add_profile_lighting: false,
      add_ceiling_profile_lights: false,
      template_type: templateType
    };
  }
  
  // Create sub-items from template
  const subItems = (template.defaultItems || []).map(item => ({
    ...createDefaultSubItem(item.item_type),
    item_name: item.item_name,
    frontage_length: item.frontage_length,
    height: item.height,
    add_profile_lighting: item.add_profile_lighting || false,
    add_mirror: item.add_mirror || false,
    selected: true
  }));
  
  return {
    room_type: 'general',
    room_name: template.label === 'Custom Room' ? `Room ${roomNumber}` : template.label,
    sub_items: subItems,
    include_false_ceiling: false,
    false_ceiling_sqft: 0,
    add_profile_lighting: false,
    add_ceiling_profile_lights: false,
    template_type: templateType,
    // Legacy fields
    frontage_length: 8,
    height: 9.5,
    kitchen_package: null
  };
};

// Create default room with sub-items (backward compatible)
export const createDefaultRoom = (roomType = 'general', roomNumber = 1, globalStyle = 'modern') => {
  if (roomType === 'kitchen') {
    return createRoomFromTemplate('kitchen', roomNumber, globalStyle);
  }
  
  if (roomType === 'general' || roomType === 'other') {
    // Custom room with default wardrobe
    return {
      id: `room-${roomNumber}-${Date.now()}`,
      room_type: 'general',
      room_name: `Room ${roomNumber}`,
      sub_items: [createDefaultSubItem('wardrobe')],
      include_false_ceiling: false,
      false_ceiling_sqft: 0,
      add_profile_lighting: false,
      add_ceiling_profile_lights: false,
      template_type: 'other',
      // Legacy fields
      frontage_length: 8,
      height: 9.5,
      kitchen_package: null
    };
  }
  
  // Try to find a matching template
  return createRoomFromTemplate(roomType, roomNumber, globalStyle);
};

// Default budget config
export const DEFAULT_BUDGET_CONFIG = {
  bedrooms: 3,
  include_living_room: true,
  kitchen_layout: 'l_shape',
  kitchen_package: 'basic',
  include_study: false,
  include_pooja: false,
  preferred_variant: 'modern'
};

// Default form data
export const DEFAULT_FORM_DATA = {
  name: '',
  email: '',
  phone: '',
  city: 'Hyderabad',
  property_type: 'residential',
  additional_notes: ''
};
